/* La Sura - bespoke enquiry wizard.
   Progressive enhancement over the native {% form 'contact' %}: with no JS the
   form renders flat and submits normally; this element turns the fieldsets into
   steps. Two guards matter:
   - After a server-side error (the form renders [data-server-errors]) we stay
     flat, so the error summary and every field are visible together. The flag
     is read from inside the form: `form` only exists within {% form %}, so an
     attribute on this element could never see the errors.
   - On submit we validate the WHOLE form and jump back to the step containing
     the first invalid field, because a required field inside a hidden step
     would otherwise fail native validation invisibly (not-focusable) and the
     submit would silently do nothing. */
class LasuraEnquiryWizard extends HTMLElement {
  connectedCallback() {
    /* After a reload, the confirmation or the error summary takes focus, so a screen reader
       user hears what happened (4.1.3, 3.3.1). Before the early returns below. */
    this.querySelector('.lasura-enquiry__success, [data-server-errors]')?.focus();
    /* Sent: the kept answers have done their job. */
    if (this.querySelector('.lasura-enquiry__success')) {
      try {
        window.localStorage.removeItem('lasura-enquiry-draft');
        window.sessionStorage.removeItem('lasura-enquiry-draft');
      } catch {}
    }
    this.form = this.querySelector('form');
    this.steps = Array.from(this.querySelectorAll('[data-wizard-step]'));
    if (!this.form || this.steps.length < 2) return;
    if (this.querySelector('[data-server-errors]')) return;

    this.index = 0;
    this.buildControls();
    this.classList.add('lasura-enquiry--stepped');
    const prefilled = this.prefillFromParams();
    this.bindFlavourChips();
    this.bindDateAndDelivery();
    this.show(0, false);
    /* The browser's own check would stop a submit (Enter on step 1, say) on a required field in a
       hidden step before onSubmit ever ran, silently. onSubmit does the whole-form check instead;
       without this script the form keeps the browser's check. */
    this.form.noValidate = true;
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    /* Answers kept between visits: its own module (assets/lasura-enquiry-draft.js), loaded
       only here, so this file stays under the page-weight budget. */
    if (this.dataset.draftSrc) {
      import(this.dataset.draftSrc).then((m) => m.bindDraft(this, prefilled)).catch(() => {});
    }
    this.track('bespoke_wizard_start', {});
  }



  buildControls() {
    const nav = document.createElement('div');
    nav.className = 'lasura-wizard__nav';

    this.progress = document.createElement('p');
    this.progress.className = 'lasura-wizard__progress';
    this.progress.setAttribute('aria-live', 'polite');

    this.backBtn = document.createElement('button');
    this.backBtn.type = 'button';
    this.backBtn.className = 'button-secondary lasura-wizard__back';
    this.backBtn.textContent = this.dataset.backLabel || '';
    this.backBtn.addEventListener('click', () => this.show(this.index - 1));

    this.nextBtn = document.createElement('button');
    this.nextBtn.type = 'button';
    this.nextBtn.className = 'button lasura-wizard__next';
    this.nextBtn.textContent = this.dataset.nextLabel || '';
    this.nextBtn.addEventListener('click', () => this.next());

    nav.append(this.backBtn, this.nextBtn);
    this.submitBtn = this.form.querySelector('.lasura-enquiry__submit');
    const anchor = this.submitBtn || this.form.lastElementChild;
    this.form.insertBefore(this.progress, this.form.querySelector('[data-wizard-step]'));
    anchor.parentNode.insertBefore(nav, anchor);
  }

  /* A missed or wrong answer: its message under the field, tied to it with aria-describedby and
     aria-invalid, and focus on the first one (teardown SVC-5). The words are section settings;
     without them, the browser's own message. Fixing the field clears it. */
  stepValid(i) {
    let first = null;
    const checks = window.lasuraEnquiryChecks;
    this.steps[i].querySelectorAll('input, select, textarea').forEach((f) => {
      if (f.checkValidity()) return checks?.clear(f);
      checks ? checks.show(this, f) : !first && f.reportValidity();
      first ??= f;
    });
    first?.focus();
    return !first;
  }

  next() {
    if (!this.stepValid(this.index)) return;
    this.show(this.index + 1);
    this.track('bespoke_wizard_step', {
      step_index: this.index + 1,
      step_name: this.legendText(this.index)
    });
  }

  show(i, focus = true) {
    this.index = Math.max(0, Math.min(i, this.steps.length - 1));
    this.steps.forEach((s, n) => { s.hidden = n !== this.index; });

    const last = this.index === this.steps.length - 1;
    this.backBtn.hidden = this.index === 0;
    this.nextBtn.hidden = last;
    if (this.submitBtn) this.submitBtn.hidden = !last;

    /* The step's legend is its visible heading and takes focus, so the progress line only counts. */
    this.progress.textContent = (this.dataset.progress || '')
      .replace('[step]', this.index + 1)
      .replace('[total]', this.steps.length);

    if (focus) {
      const legend = this.steps[this.index].querySelector('legend');
      if (legend) {
        legend.setAttribute('tabindex', '-1');
        legend.focus();
      }
    }
  }

  legendText(i) {
    const legend = this.steps[i].querySelector('legend');
    return legend ? legend.textContent.trim() : '';
  }

  onSubmit(e) {
    if (!this.form.checkValidity()) {
      e.preventDefault();
      const bad = this.steps.findIndex((s) =>
        Array.from(s.querySelectorAll('input, select, textarea')).some((f) => !f.checkValidity())
      );
      if (bad >= 0) {
        this.show(bad);
        this.stepValid(bad);
      }
      return;
    }
    this.track('bespoke_enquiry_submit', { steps: this.steps.length });
    /* The La Sura app gets the enquiry too, so the kitchen can open and quote it there (staff plan
       slice 4): the form's own answers (contact[...]) and the reference photo links, never the photo
       files. The alert on the phones still carries only the occasion and the month. A beacon still
       goes as the page moves on; the email from Shopify is unchanged. */
    try {
      const fields = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (key.startsWith('contact[') && typeof value === 'string' && value) fields.append(key, value);
      }
      navigator.sendBeacon('/apps/lasura/enquiry', fields);
    } catch {}
  }

  /* Funnel A handoff: ?occasion=Wedding&servings=25&fulfilment=Collection;
     the cake builder adds flavours, allergens and theme. */
  /* No past dates (batch 5 panel), and the venue postcode only when it's being delivered. Without
     JavaScript both stay as plain fields, and the postcode label says "if delivery". */
  bindDateAndDelivery() {
    const date = this.querySelector('#LasuraDate');
    if (date) {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      date.min = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      window.lasuraEnquiryChecks?.soon(this, date);
    }
    const postcode = this.querySelector('[data-delivery-only]');
    const radios = Array.from(this.querySelectorAll('input[name="contact[Fulfilment]"]'));
    if (!postcode || !radios.length) return;
    const sync = () => {
      const delivery = radios.some((r) => r.checked && r.value === 'Local delivery');
      postcode.hidden = !delivery;
      if (!delivery) postcode.querySelector('input').value = '';
    };
    radios.forEach((r) => r.addEventListener('change', sync));
    sync();
  }

  prefillFromParams() {
    const params = new URLSearchParams(window.location.search);
    const map = {
      occasion: '#LasuraOccasion',
      servings: '#LasuraServings',
      date: '#LasuraDate',
      /* From the cake builder's Start an enquiry button (lasura-cake-prefill.js). */
      flavours: '#LasuraFlavours',
      allergens: '#LasuraAllergens',
      theme: '#LasuraTheme'
    };
    let used = false;
    Object.entries(map).forEach(([key, sel]) => {
      const val = params.get(key);
      const field = val && this.querySelector(sel);
      if (!field) return;
      used = true;
      if (field.tagName === 'SELECT') {
        const opt = Array.from(field.options).find((o) => o.value.toLowerCase() === val.toLowerCase());
        if (opt) field.value = opt.value;
      } else {
        field.value = val;
      }
    });
    const fulfil = params.get('fulfilment');
    if (fulfil) {
      const radio = this.querySelector('input[name="contact[Fulfilment]"][value="' + CSS.escape(fulfil) + '"]');
      if (radio) {
        radio.checked = true;
        used = true;
      }
    }
    return used;
  }

  /* Quick-pick chips toggle their flavour in/out of the free-text field, so the
     posted field stays a single plain-text value and no-JS keeps a working textarea. */
  bindFlavourChips() {
    const box = this.querySelector('#LasuraFlavours');
    this.querySelectorAll('[data-flavour-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const flavour = chip.dataset.flavourChip;
        const items = box.value.split(',').map((s) => s.trim()).filter(Boolean);
        const at = items.indexOf(flavour);
        if (at >= 0) items.splice(at, 1); else items.push(flavour);
        box.value = items.join(', ');
        chip.setAttribute('aria-pressed', at < 0 ? 'true' : 'false');
      });
    });
  }

  track(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, params));
  }
}

if (!customElements.get('lasura-enquiry-wizard')) {
  customElements.define('lasura-enquiry-wizard', LasuraEnquiryWizard);
}
